import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Divider,
    IconButton,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import ErrorIcon from '@mui/icons-material/Error';
import { useSelector } from 'react-redux';
import {addFundAccountAPI, getFundAccountAPI, updateFundAccountAPI} from '../../../api/CoreMlmmgmtWebServices';
import { setBankDetailsAvailable, setMlmUserObj } from 'store/slice/EcomSlice';
import {updateMlmuserInfo} from 'components/mlmuser/MlmuserAPI';

export default function BankDetails({ onBankDetailsChange }) {
    const dispatch = useDispatch();
    const [bankDetails, setBankDetails] = useState(null);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [accountHolderName, setAccountHolderName] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [ifscCode, setIfscCode] = useState('');
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const {mlmUserObj} = useSelector((state)=>state.ecomslice.mlm);
    const [openPanAadharDialog, setOpenPanAadharDialog] = useState(false);
    const [panNumber, setPanNumber] = useState(mlmUserObj?.pan || '');
    const [aadhaarNumber, setAadhaarNumber] = useState(mlmUserObj?.aadharnumber || '');

    useEffect(() => {
        fetchBankDetails();
    }, []);
    useEffect(() => {
        
    },[mlmUserObj]);

    const fetchBankDetails = () => {
        setIsFetching(true);
        getFundAccountAPI({},
            (response) => {
                if (response && response.name) {
                    setBankDetails({
                        accountHolderName: response.name,
                        accountNumber: response.accountNumber,
                        ifscCode: response.ifscCode
                    });
                    dispatch(setBankDetailsAvailable(true));
                } else {
                    dispatch(setBankDetailsAvailable(false));
                }
            setIsFetching(false);
        }, (error) => {
            console.error('Error fetching bank details:', error);
            dispatch(setBankDetailsAvailable(false));
            setIsFetching(false);
        });
    };

    const handleOpenEditDialog = () => {
        if (bankDetails) {
            setAccountHolderName(bankDetails.accountHolderName);
            setAccountNumber(bankDetails.accountNumber);
            setIfscCode(bankDetails.ifscCode);
        } else {
            setAccountHolderName('');
            setAccountNumber('');
            setIfscCode('');
        }
        setErrors({});
        setShowSuccess(false);
        setShowError(false);
        setErrorMessage('');
        setSuccessMessage('');
        setOpenEditDialog(true);
    };

    const handleCloseEditDialog = () => {
        setOpenEditDialog(false);
        setAccountHolderName('');
        setAccountNumber('');
        setIfscCode('');
        setErrors({});
        setShowSuccess(false);
        setShowError(false);
        setErrorMessage('');
        setSuccessMessage('');
    };

    const validateForm = () => {
        const newErrors = {};

        if (!accountHolderName.trim()) {
            newErrors.accountHolderName = 'Account holder name is required';
        }

        if (!accountNumber.trim()) {
            newErrors.accountNumber = 'Account number is required';
        } else if (!/^\d{10,18}$/.test(accountNumber)) {
            newErrors.accountNumber = 'Account number must be 10-18 digits';
        }

        if (!ifscCode.trim()) {
            newErrors.ifscCode = 'IFSC code is required';
        } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifscCode.toUpperCase())) {
            newErrors.ifscCode = 'Invalid IFSC code format';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validateForm()) {
            setErrorMessage('Please fill all required fields correctly');
            return;
        }

        setIsLoading(true);
        setErrorMessage('');
        setShowError(false);
        setSuccessMessage('');
        setShowSuccess(false);

        const payload = {
            name: accountHolderName.trim(),
            accountNumber: accountNumber.trim(),
            ifscCode: ifscCode.trim().toUpperCase()
        };

        const apiCall = bankDetails ? updateFundAccountAPI : addFundAccountAPI;

        apiCall(payload, (response) => {
             dispatch(setBankDetailsAvailable(true));
            const updatedDetails = {
                accountHolderName: accountHolderName.trim(),
                accountNumber: accountNumber.trim(),
                ifscCode: ifscCode.trim().toUpperCase()
            };
            setBankDetails(updatedDetails);
            setSuccessMessage(bankDetails ? 'Bank details updated successfully!' : 'Bank details added successfully!');
            setShowSuccess(true);
            setShowError(false);
            setIsLoading(false);
            setTimeout(() => {
                handleCloseEditDialog();
            }, 2000);
        }, (error) => {
             dispatch(setBankDetailsAvailable(false));
            console.error('Error saving bank details:', error);
            // Extract message from error response
            const errorMsg = error;
            setErrorMessage(errorMsg);
            setShowError(false);
            setShowSuccess(false);
            setIsLoading(false);
        });
    };

    const handleInputChange = (setter, fieldName) => (e) => {
        let value = e.target.value;
        
        // For account number, only allow digits
        if (fieldName === 'accountNumber') {
            value = value.replace(/\D/g, '');
        }
        
        setter(value);
        if (errors[fieldName]) {
            setErrors({ ...errors, [fieldName]: '' });
        }
        setErrorMessage('');
        setShowError(false);
    };

    const DetailRow = ({ label, value }) => (
        <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={5}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {label}
                </Typography>
            </Grid>
            <Grid item xs={7}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {value || '-'}
                </Typography>
            </Grid>
        </Grid>
    );

    const handleOpenPanAadharDialog = () => {
        setPanNumber(mlmUserObj?.pan || '');
        setAadhaarNumber(mlmUserObj?.aadharnumber || '');
        setOpenPanAadharDialog(true);
    };

    const handleClosePanAadharDialog = () => {
        setOpenPanAadharDialog(false);
    };

    const handleSavePanAadhar = () => {
        // Call updateMlmuserInfo API to save PAN and Aadhaar
        updateMlmuserInfo(
            mlmUserObj.id,
            {
                pan: panNumber,
                aadharnumber: aadhaarNumber,
            },
            (response) => {
                dispatch(setMlmUserObj(response));
                setOpenPanAadharDialog(false);
            },
            (error) => {
                // Optionally, handle error (show error message)
                setOpenPanAadharDialog(false);
            }
        );
    };

    if (isFetching) {
        return (
            <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
                <CardContent>
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        py: 4,
                        gap: 2
                    }}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary">
                            Loading bank details...
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccountBalanceIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Bank Details
                            </Typography>
                        </Box>
                        {bankDetails && (
                            <IconButton
                                onClick={handleOpenEditDialog}
                                sx={{
                                    color: 'primary.main',
                                    '&:hover': {
                                        backgroundColor: 'primary.50'
                                    }
                                }}
                            >
                                <EditIcon />
                            </IconButton>
                        )}
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {bankDetails ? (
                        <Box>
                            <DetailRow label="Account Holder Name" value={bankDetails.accountHolderName} />
                            <DetailRow label="Account Number" value={bankDetails.accountNumber} />
                            <DetailRow label="IFSC Code" value={bankDetails.ifscCode} />
                        </Box>
                    ) : (
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            py: 4,
                            gap: 2
                        }}>
                            <AccountBalanceIcon sx={{ fontSize: 60, color: 'text.disabled' }} />
                            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Bank details not available
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleOpenEditDialog}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600
                                }}
                            >
                                Add Bank Details
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* PAN & Aadhaar Details Box */}
            <Card sx={{ boxShadow: 3, borderRadius: 2, mt: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            PAN & Aadhaar Details
                        </Typography>
                        <IconButton
                            onClick={handleOpenPanAadharDialog}
                            sx={{
                                color: 'primary.main',
                                '&:hover': {
                                    backgroundColor: 'primary.50'
                                }
                            }}
                        >
                            <EditIcon />
                        </IconButton>
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                PAN Number
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {mlmUserObj?.pan || '-'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                Aadhaar Number
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {mlmUserObj?.aadharnumber || '-'}
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* PAN & Aadhaar Edit Dialog */}
            <Dialog
                open={openPanAadharDialog}
                onClose={handleClosePanAadharDialog}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Edit PAN & Aadhaar Details
                        </Typography>
                        <IconButton
                            onClick={handleClosePanAadharDialog}
                            sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                    backgroundColor: 'action.hover'
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="PAN Number"
                                value={panNumber}
                                onChange={e => setPanNumber(e.target.value)}
                                inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Aadhaar Number"
                                value={aadhaarNumber}
                                onChange={e => setAadhaarNumber(e.target.value)}
                                inputProps={{ maxLength: 12 }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePanAadharDialog} color="inherit" sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSavePanAadhar}
                        variant="contained"
                        color="primary"
                        sx={{ textTransform: 'none' }}
                        disabled={!panNumber.trim() || !aadhaarNumber.trim()}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit/Add Bank Details Dialog */}
            <Dialog
                open={openEditDialog}
                onClose={handleCloseEditDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {bankDetails ? 'Edit Bank Details' : 'Add Bank Details'}
                        </Typography>
                        <IconButton
                            onClick={handleCloseEditDialog}
                            sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                    backgroundColor: 'action.hover'
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {showSuccess ? (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 4,
                            gap: 2
                        }}>
                            <SaveIcon sx={{ fontSize: 64, color: 'success.main' }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                                Success!
                            </Typography>
                            <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                                {successMessage}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Account Holder Name"
                                        value={accountHolderName}
                                        onChange={handleInputChange(setAccountHolderName, 'accountHolderName')}
                                        error={!!errors.accountHolderName}
                                        helperText={errors.accountHolderName}
                                        required
                                        disabled={isLoading}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Account Number"
                                        value={accountNumber}
                                        onChange={handleInputChange(setAccountNumber, 'accountNumber')}
                                        error={!!errors.accountNumber}
                                        helperText={errors.accountNumber}
                                        required
                                        inputProps={{ 
                                            maxLength: 18,
                                            inputMode: 'numeric',
                                            pattern: '[0-9]*'
                                        }}
                                        disabled={isLoading}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="IFSC Code"
                                        value={ifscCode}
                                        onChange={handleInputChange(setIfscCode, 'ifscCode')}
                                        error={!!errors.ifscCode}
                                        helperText={errors.ifscCode}
                                        required
                                        inputProps={{ 
                                            maxLength: 11,
                                            style: { textTransform: 'uppercase' }
                                        }}
                                        disabled={isLoading}
                                    />
                                </Grid>
                            </Grid>
                            {errorMessage && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {errorMessage}
                                </Alert>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    {showSuccess ? (
                        <Button
                            onClick={handleCloseEditDialog}
                            variant="contained"
                            color="primary"
                            sx={{ textTransform: 'none' }}
                        >
                            Close
                        </Button>
                    ) : (
                        <>
                            <Button
                                onClick={handleCloseEditDialog}
                                color="inherit"
                                sx={{ textTransform: 'none' }}
                                disabled={isLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                variant="contained"
                                color="primary"
                                disabled={isLoading}
                                startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                                sx={{ textTransform: 'none' }}
                            >
                                {isLoading ? 'Saving...' : 'Save'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}